<template>
  <div class="px-4 py-4 main-container sm:px-6">
    <div class="h-full md:flex scrollable">
      <div class="md:w-1/3 md:pr-4 scrollable">
        <h3 class="text-lg font-medium leading-6 text-gray-900">
          <router-link :to="`/patients/${patient.id}`"
            >{{ patient.first_name }} {{ patient.last_name }}</router-link
          >
        </h3>
        <div class="px-4 py-5 mt-5 bg-white border-t border-gray-200 sm:px-6">
          <div
            class="flex flex-row-reverse flex-wrap items-center justify-between -mt-4 -ml-4 sm:flex-no-wrap"
          >
            <div class="flex-shrink-0 mt-4 ml-4">
              <span class="inline-flex rounded-md shadow-sm">
                <button
                  @click="print"
                  type="button"
                  class="relative inline-flex items-center px-4 py-2 text-sm font-medium leading-5 text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-500 focus:outline-none focus:shadow-outline-teal focus:border-teal-700 active:bg-teal-700"
                >
                  Stampa Ricetta
                </button>
              </span>
              <span class="inline-flex rounded-md shadow-sm">
                <button
                  @click="save"
                  type="button"
                  class="relative inline-flex items-center px-4 py-2 text-sm font-medium leading-5 text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-500 focus:outline-none focus:shadow-outline-indigo focus:border-indigo-700 active:bg-indigo-700"
                >
                  Salva
                </button>
              </span>
            </div>
          </div>
        </div>
        <template v-if="id">
          <files
            class="mb-5"
            :patient_id="patient_id"
            :examination_id="id"
          ></files>
          <photos></photos>
        </template>
      </div>
      <div class="mt-5 md:w-2/3 scrollable md:mt-0">
        <div class="grid grid-cols-6 gap-3">
          <div class="col-span-6 sm:col-span-3 lg:col-span-2">
            <label
              for="date"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Data</label
            >
            <flat-pickr
              id="date"
              v-model="examination.date"
              :config="config"
              class="block w-full px-3 py-2 mt-1 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm form-input focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
            >
            </flat-pickr>
          </div>

          <div class="col-span-6 sm:col-span-3 lg:col-span-2">
            <label
              for="dob"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Inizio</label
            >
            <flat-pickr
              id="start"
              v-model="examination.start"
              :config="time_config"
              class="block w-full px-3 py-2 mt-1 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm form-input focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
            >
            </flat-pickr>
          </div>
          <div class="col-span-6 sm:col-span-3 lg:col-span-2">
            <label
              for="end"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Fine</label
            >
            <flat-pickr
              id="end"
              v-model="examination.end"
              :config="time_config"
              class="block w-full px-3 py-2 mt-1 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm form-input focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
            >
            </flat-pickr>
          </div>

          <div class="col-span-6">
            <label
              for="symptoms"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Diagnosi</label
            >
            <textarea
              id="symptoms"
              v-model="examination.symptoms"
              :rows="symptomsRows"
              @focus="symptomsRows = expandedRows"
              @blur="symptomsRows = defaultRows"
              class="block w-full px-3 py-2 mt-1 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm form-input focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
            >
            </textarea>
          </div>

          <div class="col-span-6">
            <label
              for="diagnosis"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Visita</label
            >
            <textarea
              id="diagnosis"
              v-model="examination.diagnosis"
              :rows="diagnosisRows"
              @focus="diagnosisRows = expandedRows"
              @blur="diagnosisRows = defaultRows"
              class="block w-full px-3 py-2 mt-1 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm form-input focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
            >
            </textarea>
          </div>

          <div class="col-span-6">
            <label
              for="price"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Prezzo</label
            >
            <input
              id="price"
              v-model="examination.price"
              class="block w-full px-3 py-2 mt-1 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm form-input focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
            />
          </div>

          <div class="col-span-6">
            <label
              for="prescription"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Ricetta</label
            >
            <textarea
              id="prescription"
              v-model="examination.prescription"
              :rows="prescriptionRows"
              @focus="prescriptionRows = expandedRows"
              @blur="prescriptionRows = defaultRows"
              class="block w-full px-3 py-2 mt-1 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm form-input focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
            >
            </textarea>
          </div>

          <div class="col-span-6">
            <label
              for="notes"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Note</label
            >
            <textarea
              id="notes"
              v-model="examination.notes"
              :rows="notesRows"
              @focus="notesRows = expandedRows"
              @blur="notesRows = defaultRows"
              class="block w-full px-3 py-2 mt-1 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm form-input focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
            >
            </textarea>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { Italian } from "flatpickr/dist/l10n/it";
import Upload from "./Upload";
import Files from "./Files";
import Photos from "./Photos";

export default {
  props: {
    patient_id: {
      required: true,
    },
    id: {
      required: false,
    },
  },

  components: {
    Files,
    Photos,
  },

  data: () => {
    return {
      loaded: false,
      defaultRows: 3,
      expandedRows: 10,
      diagnosisRows: 3,
      symptomsRows: 3,
      prescriptionRows: 3,
      notesRows: 3,
      config: {
        altFormat: "d/m/Y",
        altInput: true,
        dateFormat: "Y-m-d",
        locale: Italian,
        allowInput: true,
      },
      time_config: {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        minDate: "09:00",
        maxDate: "20:00",
        allowInput: true,
      },
    };
  },

  computed: {
    ...mapState("examination", ["examination"]),
    ...mapState("patient", ["patient"]),
  },

  methods: {
    print() {
      this.$store.dispatch("examination/PRINT", {
        patient: this.patient,
        examination: this.examination,
      });
    },
    save() {
      this.$store.dispatch("examination/SAVE", this.examination);
    },
    destroy() {
      if (confirm("Sicuro di voler cancellare la visita?")) {
        this.$store.dispatch("examination/DELETE");
        this.$router.go(-1);
      }
    },
  },
};
</script>
